"use client"

import { X } from "lucide-react"
import { type PlanDisplayData } from "@/lib/plan-display-data"

interface BenefitOption {
  label: string
  value: string
}

interface BenefitField {
  id: string
  label: string
  options: BenefitOption[]
  defaultValue: string
  fullWidth?: boolean
}

interface ViewAllModalProps {
  plan: PlanDisplayData
  benefitFields?: BenefitField[]
  configValues: Record<string, string>
  onClose: () => void
}

export function ViewAllModal({
  plan,
  benefitFields = [],
  configValues,
  onClose,
}: ViewAllModalProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative z-10 max-h-[70vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white shadow-2xl dark:bg-slate-900">
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 flex h-8 w-8 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-200"
        >
          <X className="h-4 w-4" />
        </button>

        {/* Header */}
        <div className="border-b border-slate-100 px-6 pt-6 pb-4 dark:border-slate-700">
          <p className="text-sm font-semibold text-nova-600 dark:text-nova-400">
            Plan Details
          </p>
          <h2 className="mt-0.5 text-2xl font-bold text-slate-900 dark:text-white">
            {plan.name}
          </h2>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            {plan.productName}
          </p>
        </div>

        {/* Plan meta */}
        <div className="grid grid-cols-3 gap-px bg-slate-100 dark:bg-slate-700">
          <div className="bg-white px-4 py-3 dark:bg-slate-900">
            <p className="text-[10px] text-slate-400">TPA</p>
            <p className="text-sm font-bold text-slate-900 dark:text-white">
              {plan.tpa}
            </p>
          </div>
          <div className="bg-slate-50 px-4 py-3 dark:bg-slate-800">
            <p className="text-[10px] text-slate-400">Annual Medical Limit</p>
            <p className="text-sm font-bold text-slate-900 dark:text-white">
              {plan.annualMedicalLimit}
            </p>
          </div>
          <div className="bg-slate-50 px-4 py-3 dark:bg-slate-800">
            <p className="text-[10px] text-slate-400">Coverage Area</p>
            <p className="text-sm font-bold text-slate-900 dark:text-white">
              {plan.coverageArea}
            </p>
          </div>
        </div>

        {/* All benefit fields */}
        <div className="p-6">
          <p className="mb-4 text-xs font-semibold tracking-wide text-slate-400 uppercase dark:text-slate-500">
            Plan Benefits
          </p>
          {benefitFields.length === 0 ? (
            <div className="py-4 text-center text-sm text-slate-500">
              No benefit details available for this plan.
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-x-8 gap-y-4">
              {benefitFields.map((field) => (
                <div
                  key={field.id}
                  className={field.fullWidth ? "col-span-2" : ""}
                >
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {field.label}
                  </p>
                  <p className="mt-0.5 text-sm font-bold text-slate-900 dark:text-white">
                    {configValues[field.id] ?? field.defaultValue}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end border-t border-slate-100 px-6 py-4 dark:border-slate-700">
          <button
            onClick={onClose}
            className="rounded-full border border-slate-300 px-6 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}
